"use client";

import { useEffect, useRef } from "react";

import  Navbar4  from "../IVRVue/Navbar4";

import { CheckCircle,ArrowRight} from "lucide-react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faRocket, faUsers, faBrain } from "@fortawesome/free-solid-svg-icons";


const AboutPageIVR = () => {
  
  const keyPoints = [
    "Industry-leading expertise in emerging technologies",
    "Customized solutions tailored to your specific business needs",
    "Comprehensive training and support throughout implementation",
    "Continuous research and development to stay ahead of market trends",
    "Strategic partnerships with global technology leaders",
  ];

  
  const aboutRef = useRef(null);

 
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
       
        if (entry.isIntersecting) {
          entry.target.classList.add("animate");
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = aboutRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    // Cleanup function to unobserve the element
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    // Main container with a dark background
    <main className="bg-[#0c1c5c] text-white">
      <Navbar4 />
      
      {/* --- Hero Section --- */}
      <section className="text-center py-20 px-4">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
          About MNR Technologies
        </h1>
        <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
          We believe innovation isn't just about technology — it's about solving the right problems, anticipating change, and empowering people. Our team of experts brings a blend of industry knowledge, technical excellence, and a relentless commitment to quality, ensuring every solution we deliver is impactful, sustainable, and aligned with your long-term vision.
        </p>
      </section>

      {/* --- "Who We Are" Section --- */}
      <section ref={aboutRef} className="py-24 sm:py-32 opacity-0 transform translate-y-10 transition-all duration-1000 ease-out [&.animate]:opacity-100 [&.animate]:translate-y-0">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-x-16 gap-y-12 items-center">
          
          {/* Text Content Column */}
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-4xl">
              Who We Are
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-300">
              MNR Technologies is a leading provider of innovative technology solutions, dedicated to helping businesses navigate the complexities of digital transformation. With expertise spanning healthcare, banking, AI, cloud computing, and cybersecurity, we deliver tailored solutions that drive growth and operational excellence.
            </p>
            <p className="mt-4 text-lg leading-8 text-gray-300">
              Our team of experienced professionals combines technical expertise with deep industry knowledge to develop solutions that address your unique challenges and opportunities. We're committed to staying at the forefront of technological advancement through continuous research and development.
            </p>
            <div className="mt-10 space-y-4">
              {keyPoints.map((point, index) => (
                <div className="flex items-center gap-x-4" key={index}>
                  <CheckCircle
                    className="h-6 w-6 flex-none text-blue-400"
                    aria-hidden="true"
                  />
                  <span className="text-gray-300">{point}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Image Column */}
          <div className="flex justify-center">
            <img
              src="/about.png"
              alt="A modern office with people working on computers"
              className="rounded-xl shadow-2xl shadow-blue-500/20 w-full max-w-lg"
              onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400'; }}
            />
          </div>

        </div>
      </section>
      
    

      {/* --- Culture Section --- */}
      <div className="bg-[#0c1c5c] flex items-center justify-center p-6 py-20">
        <div className="max-w-7xl w-full flex flex-col lg:flex-row gap-10 lg:gap-20 items-center">
          <section className="flex flex-col max-w-xl space-y-6">
            <h2 className="text-white text-4xl font-semibold tracking-wide">Our Culture</h2>
            <p className="text-gray-300 text-md leading-relaxed max-w-md">
              We are a diverse team with a deep commitment to innovation and collaboration. We believe in a culture of continuous learning, growth mindset, and open communication.
            </p>
            <div className="space-y-6">
              <article className="bg-white rounded-lg p-6 flex items-start gap-6 max-w-md shadow-md hover:shadow-blue-500/30 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-md flex items-center justify-center">
                  <FontAwesomeIcon icon={faRocket} className="text-blue-800 text-4xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Innovation First</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
                    We foster a culture of continuous innovation, encouraging our team to push boundaries.
                  </p>
                </div>
              </article>

              <article className="bg-white rounded-lg p-6 flex items-start gap-6 max-w-md shadow-md hover:shadow-blue-500/30 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-md flex items-center justify-center">
                  <FontAwesomeIcon icon={faUsers} className="text-blue-800 text-4xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Collaboration</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
                    Our success is built on strong teamwork and open communication across all levels.
                  </p>
                </div>
              </article>

              <article className="bg-white rounded-lg p-6 flex items-start gap-6 max-w-md shadow-md hover:shadow-blue-500/30 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-md flex items-center justify-center">
                  <FontAwesomeIcon icon={faBrain} className="text-blue-800 text-4xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Growth Mindset</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
                    We invest in our team's professional development and encourage learning at every step.
                  </p>
                </div>
              </article>
            </div>
          </section>

          <section className="flex-1 flex justify-center items-center">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
              alt="Three people working on laptops"
              className="rounded-xl shadow-lg h-full object-cover max-h-[600px]"
            />
          </section>
        </div>
      </div>
      
      {/* --- Call to Action Section --- */}
      <div className="text-center py-20 px-4">
        <h2 className="text-3xl md:text-4xl font-bold">Ready to Transform Your Business?</h2>
        <p className="mt-4 max-w-2xl mx-auto text-gray-300">
          Contact us today to discuss how our solutions and services can help you achieve your business goals.
        </p>
        <a href="/book" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-transform duration-300 hover:scale-105">
          Get in Touch
          <ArrowRight className="h-5 w-5" />
        </a>
      </div>
    </main>
  );
};

export default AboutPageIVR;
